import { useEffect, useRef, useState } from 'react'
import { Search, Loader2, User, ArrowUpRight, CornerDownLeft, Command, Clock, X } from 'lucide-react'
import { apiGet } from '../lib/api.js'
import { useMe } from '../lib/me.js'
import { getRecentPatients, addRecentPatient, removeRecentPatient, clearRecentPatients } from '../lib/recentPatients.js'
import { MIN_PATIENT_SEARCH } from '../lib/patientSearch.js'
import { apiErrorText } from '../lib/apiError.js'

// Xano rejects a shorter term with a 500 — see lib/patientSearch.js.
const MIN_QUERY = MIN_PATIENT_SEARCH
const DEBOUNCE_MS = 220
const RECENT_LIMIT = 3

export default function SearchOverlay({ onClose }) {
  const me = useMe()
  const userKey = me?.email || (me?.employee_id ? `emp:${me.employee_id}` : '')
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [recents, setRecents] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)
  const reqIdRef = useRef(0)
  const listRef = useRef(null)

  // Autofocus on open.
  useEffect(() => { inputRef.current?.focus() }, [])

  // Load this user's recently-opened patients (shown until they start typing).
  useEffect(() => { setRecents(getRecentPatients(userKey, RECENT_LIMIT)) }, [userKey])

  const queryLen = q.trim().length
  const showingRecents = queryLen < MIN_QUERY && recents.length > 0
  // the list the keyboard / Enter operate on (results while searching, else recents)
  const items = queryLen >= MIN_QUERY ? results : (showingRecents ? recents : [])

  // Debounced search against Xano.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const query = q.trim()
    setHighlight(0)
    if (query.length < MIN_QUERY) {
      setResults([])
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    debounceRef.current = setTimeout(async () => {
      const myReqId = ++reqIdRef.current
      try {
        const data = await apiGet('/api/patients', { search: query, per_page: 15 })
        if (myReqId !== reqIdRef.current) return
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : [])
        setResults(items)
      } catch (e) {
        if (myReqId !== reqIdRef.current) return
        setError(apiErrorText(e, 'ค้นหาไม่สำเร็จ'))
        setResults([])
      } finally {
        if (myReqId === reqIdRef.current) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(debounceRef.current)
  }, [q])

  function openPatient(p) {
    const id = p?.id ?? p?.patient_id
    if (!id) return
    addRecentPatient(userKey, p)            // remember for next time
    window.open(`/patient/${id}/info`, '_blank', 'noopener,noreferrer')
    onClose()
  }

  const removeRecent = (id) => {
    removeRecentPatient(userKey, id)
    setRecents((r) => r.filter((x) => String(x.id) !== String(id)))
    setHighlight(0)
  }
  const clearRecents = () => { clearRecentPatients(userKey); setRecents([]); setHighlight(0) }

  // Keyboard navigation inside the overlay (operates on whichever list is shown).
  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((i) => Math.min(i + 1, Math.max(items.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const p = items[highlight]
      if (p) openPatient(p)
    }
  }

  // Scroll highlighted item into view.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${highlight}"]`)
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' })
    }
  }, [highlight])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[14vh] px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onMouseDown={onClose} />

      {/* Dialog */}
      <div
        className="relative w-full max-w-[640px] bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 overflow-hidden animate-ss-pop-in"
        role="dialog"
        aria-modal="true"
      >
        {/* Search input */}
        <div className="relative border-b border-slate-100">
          <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" strokeWidth={1.75} />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="ค้นหา ชื่อ · นามสกุล · HN · เบอร์โทร · อีเมล"
            className="w-full pl-12 pr-14 py-4 text-[16px] bg-transparent placeholder:text-slate-400 outline-none"
          />
          {loading && <Loader2 size={16} className="absolute right-5 top-1/2 -translate-y-1/2 text-brand-500 animate-spin" />}
        </div>

        {/* Body */}
        <div ref={listRef} className="max-h-[55vh] overflow-y-auto">
          {error && (
            <div className="px-5 py-4 text-[14px] text-rose-700 bg-rose-50">{error}</div>
          )}

          {!error && queryLen < MIN_QUERY && (
            showingRecents ? (
              <div className="py-1.5">
                <div className="flex items-center justify-between px-4 pt-2 pb-1">
                  <div className="flex items-center gap-1.5 text-[12px] uppercase tracking-[0.12em] font-semibold text-slate-400">
                    <Clock size={12} strokeWidth={2} /> ล่าสุด
                  </div>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={clearRecents}
                    className="text-[12.5px] text-slate-400 hover:text-rose-600 transition-colors">ล้าง</button>
                </div>
                <ul>
                  {recents.map((p, i) => (
                    <ResultRow
                      key={p.id}
                      p={p}
                      idx={i}
                      active={i === highlight}
                      recent
                      onPick={() => openPatient(p)}
                      onHover={() => setHighlight(i)}
                      onRemove={() => removeRecent(p.id)}
                    />
                  ))}
                </ul>
              </div>
            ) : (
              <div className="px-6 py-10 text-center">
                <div className="text-[14px] text-slate-400">พิมพ์อย่างน้อย {MIN_QUERY} ตัวอักษรเพื่อค้นหา</div>
                <div className="text-[13px] text-slate-300 mt-1">ค้นหาได้จากชื่อ · นามสกุล · HN · เบอร์โทร · อีเมล</div>
              </div>
            )
          )}

          {!error && q.trim().length >= MIN_QUERY && results.length === 0 && !loading && (
            <div className="px-6 py-10 text-center text-[14px] text-slate-400">
              ไม่พบผู้ป่วยที่ตรงกับ "<span className="text-slate-700 font-medium">{q}</span>"
            </div>
          )}

          {!error && results.length > 0 && (
            <ul className="py-1.5">
              {results.map((p, i) => (
                <ResultRow
                  key={p.id ?? p.patient_id ?? i}
                  p={p}
                  idx={i}
                  active={i === highlight}
                  onPick={() => openPatient(p)}
                  onHover={() => setHighlight(i)}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Footer hints */}
        <div className="flex items-center justify-between px-5 py-2.5 bg-slate-50/60 border-t border-slate-100 text-[13px] text-slate-500">
          <div className="flex items-center gap-3">
            <Hint>↑↓</Hint>
            <span>เลือก</span>
            <Hint><CornerDownLeft size={11} strokeWidth={2} /></Hint>
            <span>เปิดเคส</span>
          </div>
          <div className="flex items-center gap-2">
            <Hint>esc</Hint>
            <span>ปิด</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function Hint({ children }) {
  return (
    <span className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded bg-white border border-slate-200 text-slate-600 font-mono text-[12px]">
      {children}
    </span>
  )
}

function ResultRow({ p, idx, active, recent, onPick, onHover, onRemove }) {
  const fullName = [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim() || p.fullname || '—'
  const nick = p.nickname ? ` (${p.nickname})` : ''
  const hn = p.hn || p.HN || ''
  const phone = p.phone || p.tel || ''
  const branch = p.branch_name || p.branch?.name || ''
  const avatar = p.line_picture_url || ''
  return (
    <li className="relative">
      <button
        type="button"
        data-idx={idx}
        onClick={onPick}
        onMouseEnter={onHover}
        className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${recent ? 'pr-12' : ''} ${
          active ? 'bg-brand-50' : 'hover:bg-slate-50'
        }`}
      >
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-none ring-1 overflow-hidden transition-colors ${
          active ? 'bg-brand-100 text-brand-700 ring-brand-200' : 'bg-slate-100 text-slate-500 ring-slate-200'
        }`}>
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          ) : (
            <User size={15} strokeWidth={1.75} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14.5px] font-medium text-slate-900 truncate">
            {fullName}<span className="text-slate-400 font-normal">{nick}</span>
          </div>
          <div className="text-[13.5px] text-slate-500 truncate flex items-center gap-2">
            {hn && <span className="font-mono">HN {hn}</span>}
            {hn && (phone || branch) && <span className="text-slate-300">·</span>}
            {phone && <span>{phone}</span>}
            {phone && branch && <span className="text-slate-300">·</span>}
            {branch && <span>{branch}</span>}
          </div>
        </div>
        {!recent && <ArrowUpRight size={14} className={`flex-none transition-colors ${active ? 'text-brand-600' : 'text-slate-300'}`} />}
      </button>
      {recent && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          title="ลบออกจากล่าสุด"
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-colors"
        >
          <X size={14} />
        </button>
      )}
    </li>
  )
}
